<script setup lang="ts">
import Button from '@components/Button.vue';
import IconXCircleFill from '@icons/ph_x-circle-fill.svg';
import Icon from '@components/Icon.vue';
import InputAddress from '@components/InputAddress.vue';
import Spinner from '@components/Spinner.vue';
import { storeToRefs } from 'pinia';
import SliderModal from '@components/SliderModal.vue';
import { trans } from '@elements/translation';
import { ref } from 'vue';
import { useCustomMarkerStore } from '../../stores/useCustomMarkerStore.ts';

const customMarkerStore = useCustomMarkerStore();

const { close, save } = customMarkerStore;
const { isOpen, googleAddress, addressName, savedDealUdurMarker, isSaving } = storeToRefs(customMarkerStore);

const saveDisabled = ref(false);

const onAddressError = (value: boolean): void => {
  saveDisabled.value = value;
};

const clear = (): void => {
  addressName.value = null;
};
</script>

<template>
  <SliderModal
    :is-open="isOpen"
    class="custom-marker-slider"
    @close="close"
  >
    <template #header>
      {{ trans(savedDealUdurMarker ? 'selection_item.custom_marker.slider.title.edit' : 'selection_item.custom_marker.slider.title.add') }}
    </template>

    <template #body>
      <div class="custom-marker-slider__body slider-body">
        <div class="slider-body__field">
          <label class="input-label" for="marker-address">{{ trans('selection_item.custom_marker.form.address.label') }}</label>
          <InputAddress
            id="marker-address"
            v-model="googleAddress"
            :placeholder="trans('selection_item.custom_marker.form.address.placeholder')"
            @error="onAddressError"
          />
        </div>
        <div class="slider-body__field">
          <label class="input-label" for="marker-name">{{ trans('selection_item.custom_marker.form.name.label') }}</label>
          <div class="input-text">
            <input
              id="marker-name"
              v-model="addressName"
              type="text"
              :placeholder="trans('selection_item.custom_marker.form.name.placeholder')"
            >
            <div class="input-text__clear" @click="clear">
              <Icon :svg="IconXCircleFill" :width="24" :height="24" />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="custom-marker-slider__footer slider-footer">
        <Button
          variant="secondary"
          size="jumbo"
          class="slider-footer__button"
          @click="close"
        >
          {{ trans('global.cancel') }}
        </Button>
        <Button
          variant="primary"
          size="jumbo"
          class="slider-footer__button"
          :disabled="saveDisabled"
          @click="save"
        >
          <spinner v-if="isSaving" />
          <template v-else>
            {{ trans('global.validate') }}
          </template>
        </Button>
      </div>
    </template>
  </SliderModal>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.custom-marker-slider {
  &__body, .slider-body {
    padding: rem(16);
    display: flex;
    justify-content: start;
    flex-direction: column;
    gap: rem(24);

    &__field {
      margin-bottom: 0;

      :deep(.input-text) {
        position: relative;

        input {
          border-radius: rem(8);
        }

        .input-text__clear {
          display: flex;
          position: absolute;
          width: rem(48);
          height: rem(48);
          right: 0;
          top: 0;
          justify-content: center;
          align-items: center;
          cursor: pointer;
        }
      }
    }
  }

  &__footer, .slider-footer {
    display: flex;
    gap: rem(16);
    justify-content: space-between;
  }
}
</style>
